import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { useProduct } from '../../context/ProductContext';
import { SET_PRODUCT_ENABLED } from '../../graphql/mutations';

function Status({ on }: { on: boolean }) {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        font: '500 13px/1 var(--ink-font)',
        color: on ? 'var(--ink-green-ink)' : 'var(--ink-ink3)',
      }}
    >
      {on ? <span className="ink-dot" style={{ background: 'var(--ink-green)' }} /> : null}
      {on ? 'On' : 'Off'}
    </span>
  );
}

/**
 * TinyCMS → Add-ons. Atomize is the Atomizer add-on (research, the assembler,
 * atomizing an article). An account that already has the atomizer product has
 * it on, and can switch it off (and back on) here — Atomizer is parked for a
 * later launch, so it can't be added by an account that never had it. Amplify
 * is PostMaster, and stays on its own shell.
 */

export const CmsAddons: React.FC = () => {
  const navigate = useNavigate();
  const { selectedAccount, accountId, refetchAccounts } = useAccount();
  const { product, entitled } = useProduct();
  const products = selectedAccount?.products;
  const atomizeOn =
    products == null
      ? entitled.includes('atomizer')
      : products.some((p) => p.product === 'atomizer' && p.status === 'active');
  // Held but switched off: it can be switched back on.
  const atomizeHeld = Boolean(products?.some((p) => p.product === 'atomizer'));
  const [setEnabled, { loading: switching }] = useMutation(SET_PRODUCT_ENABLED);
  const [switchError, setSwitchError] = useState('');

  const switchAtomize = async (enabled: boolean) => {
    if (
      !enabled &&
      !window.confirm(
        'Turn off Atomize? Research and the Assembler stop working, and articles are no longer atomized. Your articles, pages and site are not affected, and you can turn it back on here.'
      )
    ) {
      return;
    }
    setSwitchError('');
    try {
      await setEnabled({ variables: { accountId, product: 'atomizer', enabled } });
      await refetchAccounts();
    } catch (err) {
      setSwitchError(err instanceof Error ? err.message : 'Could not change Atomize');
    }
  };
  const amplifyOn = entitled.includes('reach');
  const hostIsAmplify = product === 'reach';

  return (
    <main className="ink-body">
      <h1 className="ink-h1">Add-ons</h1>
      <p className="ink-lede">Optional capabilities built on your published content.</p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 20,
          marginTop: 36,
        }}
      >
        {hostIsAmplify ? (
          <div
            className="ink-card"
            style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ font: '600 22px/1.2 var(--ink-font)' }}>TinyCMS</div>
              <Status on={atomizeOn} />
            </div>
            <p
              style={{ margin: 0, font: '400 15px/1.55 var(--ink-font)', color: 'var(--ink-ink2)' }}
            >
              The library, the public site, and what needs you before it goes live.
            </p>
            <div style={{ marginTop: 'auto', display: 'flex', gap: 16 }}>
              {atomizeOn ? (
                <>
                  <button
                    type="button"
                    className="ink-site-link"
                    onClick={() => navigate('/today')}
                  >
                    Open Today →
                  </button>
                  <button
                    type="button"
                    className="ink-site-link"
                    onClick={() => navigate('/content')}
                  >
                    Content →
                  </button>
                  <button type="button" className="ink-site-link" onClick={() => navigate('/site')}>
                    Site →
                  </button>
                </>
              ) : (
                <span className="ink-muted">Not on this account.</span>
              )}
            </div>
          </div>
        ) : null}

        <div
          className="ink-card"
          style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ font: '600 22px/1.2 var(--ink-font)' }}>Atomize</div>
            <Status on={atomizeOn} />
          </div>
          <p style={{ margin: 0, font: '400 15px/1.55 var(--ink-font)', color: 'var(--ink-ink2)' }}>
            Breaks articles into verified facts and entities so LLMs can cite them. Includes
            Research and the Assembler.
          </p>
          <div style={{ marginTop: 'auto', display: 'flex', gap: 16 }}>
            {atomizeOn ? (
              <>
                <button
                  type="button"
                  className="ink-site-link"
                  onClick={() => navigate('/research')}
                >
                  Open Research →
                </button>
                <button
                  type="button"
                  className="ink-site-link"
                  onClick={() => navigate('/assembler')}
                >
                  Open Assembler →
                </button>
                <button
                  type="button"
                  className="ink-site-link"
                  data-muted="true"
                  style={{ marginLeft: 'auto' }}
                  disabled={switching}
                  onClick={() => void switchAtomize(false)}
                >
                  {switching ? 'Turning off…' : 'Turn off'}
                </button>
              </>
            ) : atomizeHeld ? (
              <>
                <span className="ink-muted">Turned off.</span>
                <button
                  type="button"
                  className="ink-site-link"
                  disabled={switching}
                  onClick={() => void switchAtomize(true)}
                >
                  {switching ? 'Turning on…' : 'Turn on'}
                </button>
              </>
            ) : (
              <span className="ink-muted">Not on this account.</span>
            )}
          </div>
          {switchError ? (
            <p className="ink-site-hint" style={{ color: 'var(--ink-red)', margin: 0 }}>
              {switchError}
            </p>
          ) : null}
        </div>

        {hostIsAmplify ? null : (
          <div
            className="ink-card"
            style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ font: '600 22px/1.2 var(--ink-font)' }}>Amplify</div>
              <Status on={amplifyOn} />
            </div>
            <p
              style={{ margin: 0, font: '400 15px/1.55 var(--ink-font)', color: 'var(--ink-ink2)' }}
            >
              Plan and run social campaigns around the topics you publish. Agents draft the posts,
              and you approve them on a calendar.
            </p>
            <div style={{ marginTop: 'auto', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {amplifyOn ? (
                <>
                  <button
                    type="button"
                    className="ink-site-link"
                    onClick={() => navigate('/amplify')}
                  >
                    Open Amplify →
                  </button>
                  <button
                    type="button"
                    className="ink-site-link"
                    onClick={() => navigate('/calendar')}
                  >
                    Calendar →
                  </button>
                  <button
                    type="button"
                    className="ink-site-link"
                    onClick={() => navigate('/projects')}
                  >
                    Projects →
                  </button>
                  <button
                    type="button"
                    className="ink-site-link"
                    onClick={() => navigate('/automation-rules')}
                  >
                    Automations →
                  </button>
                </>
              ) : (
                <span className="ink-muted">Not on this account.</span>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};
